/**
 * The admin shell's horizontal grid. One place, because it used to be four.
 *
 * WHY THIS EXISTS
 * ---------------
 * Owner review 2026-08-01: the top nav's "Dashboard" did not line up with the page
 * title under it, collapsing the rail did not fix it, and the header's indent did not
 * match the page gutter. Measured (`scripts/audit_shell_alignment.py`), the shell had
 * FOUR horizontal origins written in three files plus 92 page components:
 *
 *   logo left edge   ≈ 24   — `(240 − renderedLogoWidth) / 2`, because the logo was
 *                             CENTRED in a 240 box, so it differed between a
 *                             single-product tenant and a bundle tenant
 *   first nav item   = 252  — 240 + a hardcoded 12, and 240 + 36 once the overflow
 *                             arrow appeared, so adding a nav item moved every label
 *   rail             = 64 / 240 in SideMenu, and 56 / 232 AGAIN in MainLayout for the
 *                             collapse chevron — a second copy, offset by 8
 *   page content     = rail + 42, with `px: '42px'` repeated in 92 files
 *
 * Three of those are on screen at once and no two were equal. Nothing was broken;
 * everything was arbitrary, which is what the eye reports as unresolved.
 *
 * THE GRID
 * --------
 * One gutter for the header AND the content, on the 8px spacing grid the rest of the
 * app uses (42 was 5.25 units — a number with no origin). The logo block is exactly
 * the expanded rail's width, with the logo LEFT-ALIGNED at the gutter, so:
 *
 *   x = GUTTER          the logo, the rail's labels, and a page on a rail-less route
 *   x = RAIL.expanded + GUTTER   the first nav item AND the page title, when the
 *                                rail is expanded — the alignment that was asked for
 *
 * When the rail collapses the whole content column shifts left together — title,
 * cards, everything — which is coherent motion rather than misalignment, and is what
 * every shell with a collapsing rail does. Exact header↔content alignment in EVERY
 * rail state is not reachable while the header is a full-width band and the rail
 * pushes content: header origins are measured from the viewport, page origins from
 * the content area. It would need either an icon-only logo mark (this app ships only
 * wordmarks) or an overlay rail (which would cover a dense admin page). That trade is
 * written up in `docs/audits/SHELL_ALIGNMENT_2026-08-01.md`.
 *
 * MAINTENANCE
 * -----------
 * Read these; never re-type them. `src/i18n/shellMetrics.test.ts` fails on a rail
 * width or a page gutter written as a literal anywhere else.
 */

/** Fixed header band height. `<main>` offsets by this. */
export const HEADER_HEIGHT = 64;

/**
 * Product-logo height in the header band.
 *
 * ONE value for every tenant. It was 32 for a single licence and 26 for each half
 * of a side-by-side bundle pair, so a multi-org admin watched the mark change size
 * on every switch — and the pair's `maxWidth: 90` silently overrode that 26,
 * rendering the two wordmarks 18.6px and 19.8px tall beneath a 22px divider that
 * stood taller than both.
 *
 * 28 against a 64px band, and it must stay below `(RAIL.expanded - GUTTER) /
 * widest-aspect` so a width cap can never bite: a cap that bites overrides `height`
 * through objectFit and hides the fact that it did.
 */
export const LOGO_HEIGHT = 28;

/**
 * The side rail's two widths. The collapsed width is also the minimum a menu item
 * needs to show its icon centred.
 */
export const RAIL = {
  expanded: 240,
  collapsed: 64,
} as const;

/**
 * The single horizontal inset, used by the header band and the content area. On the
 * 8px grid (4 units).
 *
 * NOT the rail's items: a rail ICON sits at `RAIL_ICON_INSET` (20), derived from
 * centring it in the collapsed rail so the icon column does not move when the rail
 * collapses. The two answer different questions and this comment used to claim
 * otherwise (corrected 2026-08-05).
 */
export const GUTTER = 32;

/**
 * Left inset of a rail item's ICON when the rail is expanded.
 *
 * Derived, not chosen: a 24px icon centred in the collapsed rail sits at
 * `(64 − 24) / 2 = 20`, so using the same inset when expanded means the icon column
 * does not move when the rail collapses — only the labels go. It is deliberately NOT
 * `GUTTER`; the rail's inset answers "where does a centred icon land", the gutter
 * answers "how far in does content start".
 */
export const RAIL_ICON_INSET = (RAIL.collapsed - 24) / 2;

/**
 * Widest product wordmark at LOGO_HEIGHT, rounded up to the 8px grid.
 *
 * qutime-logo.png is 1054x218 (aspect 4.835), the widest of the three, so at 28px it
 * renders 135.4px. 136 rounds that to the grid.
 *
 * A FIXED number rather than the logo's actual width: the audit's option C says the
 * nav strip "follows the logo's natural width", which taken literally would move the
 * nav 8px between a QuTime tenant and a PureOA one. Deriving it from the WIDEST mark
 * keeps the header's second origin still while removing the dead space.
 */
export const LOGO_ZONE = 136;

/**
 * Left edge of the first top-nav item.
 *
 * `GUTTER + LOGO_ZONE + GUTTER` = 200. This completes the audit's recommendation
 * (`docs/audits/SHELL_ALIGNMENT_2026-08-01.md`, option C) — the header keeps its own
 * rhythm as a global band, its indent equals the page gutter, and the nav follows the
 * mark.
 *
 * It replaces `RAIL.expanded + GUTTER` = 272, which was option A's rule applied on
 * top of option C's logo rule. That hybrid paid A's cost without A's benefit: A also
 * required binding the logo zone to the rail's width so it animates 240 -> 64 on
 * collapse and shows an icon mark when narrow, and that half was never built. So the
 * nav sat at 272 to align with a page title it only met when the rail was expanded —
 * never on `/dashboard`, which is rail-less and the most-visited page in the product.
 *
 * The cost of the hybrid was measurable, not merely aesthetic. It left 104.6px of
 * dead space between a QuTime mark and the first nav item (112.8 for PureOA), and it
 * overflowed: six nav labels in French come to ~691px, so 272 + 691 + the ~320px
 * right-hand cluster exceeds 1280 and the scroll arrows appear on the 13" baseline.
 * At 200 the same row needs ~1211px and fits.
 *
 * A header nav item is not expected to align with a page title — Stripe, Vercel,
 * GitHub and Notion all decline to — but a header's gutter IS expected to equal the
 * page's, and it does.
 */
export const NAV_ORIGIN = GUTTER + LOGO_ZONE + GUTTER;

/** Rail width for a collapse state. */
export function railWidth(collapsed: boolean): number {
  return collapsed ? RAIL.collapsed : RAIL.expanded;
}

/**
 * The dialog's chrome axis — where a title, a body and an action row all begin.
 *
 * WHY THIS EXISTS
 * ---------------
 * Owner report 2026-08-08, four screenshots. Measured off them at 2x with the
 * dialog's inner edge as origin: the DialogTitle sat at 16px (`sx={{ p: 2 }}`)
 * while the field borders under it sat at 24px (`sx={{ p: 3 }}`), so the title
 * aligned with nothing. Repo-wide there were 349 dialog-chrome elements and 204
 * of them declared their own inset — ~50 titles at 16, 66 bodies and 78 action
 * rows at 24. Same shape as the shell's four origins above, one level down, and
 * the same cause: an inset that is a per-call-site decision instead of a token.
 *
 * 24 is not a new opinion. It is MUI's own default for DialogTitle
 * (`padding: '16px 24px'`) and DialogContent (`'20px 24px'`), and what the
 * majority already hand-wrote. Read from node_modules rather than memory.
 *
 * LOAD-BEARING
 * ------------
 * `MuiDialogActions`' MUI default is `padding: 8`, so a call site that drops its
 * `px: 3` gets the gutter only because `App.tsx` declares this.
 * `src/i18n/dialogAxis.test.ts` asserts the theme block AND the absence of
 * call-site overrides together; either half alone is vacuous.
 */
export const DIALOG_GUTTER = 24;

/**
 * How far MUI insets the TEXT inside an outlined field, and therefore where content
 * INSIDE a box starts.
 *
 * `OutlinedInput` is `padding: '16.5px 14px'`, so a field's floating label and its
 * value sit 14px in from the border. That is the box's own internal padding and it
 * stays.
 *
 * WHAT CHANGED, AND WHY THE EARLIER VERSION OF THIS COMMENT WAS WRONG
 * ------------------------------------------------------------------
 * This comment used to say that contained `FormHelperText` is `marginLeft: 14` "so a
 * field's label, value and helper line all share one axis — that is correct and must
 * not be fixed". The owner overruled it on 2026-08-09 with four screenshots, and they
 * are right on the evidence. Measured off them at 2x, dialog inner edge as origin:
 *
 *   dialog title              30   ← after §17's fix
 *   field borders             30
 *   value / floating label    59   ← 14px inside the box, correct
 *   helper text UNDER a box   59   ← the odd one out
 *
 * The reasoning that made 14 look right was that the helper describes the value. But
 * the value is INSIDE a box and the helper is OUTSIDE it, so the eye has no line to
 * follow between them: what it compares the helper to is the border it hangs under.
 * Of everything outside the boxes — title, field borders, action row, section cards —
 * the helper was the only element not on the shared axis.
 *
 * So the app's rule is now two levels with one test each:
 *
 *   OUTSIDE a box  →  the chrome axis (`DIALOG_GUTTER`). Title, field borders,
 *                     helper text, actions.
 *   INSIDE a box   →  `FIELD_TEXT_INSET`. A field's label and value, and a
 *                     hand-rolled section card's own content, which is a box too.
 *
 * `App.tsx` sets `MuiFormHelperText` to `marginLeft/Right: 0` for the first half.
 * This constant remains for the second.
 */
export const FIELD_TEXT_INSET = 14;

/** Dashboard widget chrome: the drag handle's geometry, and the gutter DERIVED from it.
 *
 * The handle sits FIRST IN THE FLOW of the title bar, as a flex sibling of the title. That is the
 * whole design: siblings on one flex line are centred by the same `alignItems: 'center'`, so they
 * cannot disagree vertically. The previous version positioned the handle absolutely and centred it
 * on the bar's padding box while the title centred on the content box — two centring rules for one
 * row, which the owner spotted immediately ("跟标题是高度不对齐的，不居中").
 *
 * And because the handle is in the flow, the title starts AFTER it — so the body's gutter must equal
 * the handle's full width or the title and the body sit on different axes, which was the original
 * complaint. The equality is asserted in `widgetContentAxis.test.ts` rather than trusted to this
 * comment.
 *
 *   HANDLE_INSET  4px   the bar's own left padding, before the handle
 *   HANDLE_PAD    4px   padding around the icon, on each side (its hover target)
 *   HANDLE_ICON  20px   the glyph
 *   ----------------------------------------------------------------
 *   GUTTER       32px   = 4 + 4 + 20 + 4, i.e. where the title and the body BOTH begin
 */
export const WIDGET_HANDLE_INSET = 0.5;
export const WIDGET_HANDLE_PAD = 0.5;
export const WIDGET_HANDLE_ICON = 20;
export const WIDGET_GUTTER = 4;

/** The one OPTICAL correction in the widget title bar, in CSS px, applied to the drag handle.
 *
 * Geometric centring is already correct and is not the problem: the bar's single
 * `alignItems: 'center'` centres the handle's box and the title's em box on the same line. But TEXT
 * INK is not centred inside its own em box — the baseline sits below the box's middle, so a title
 * with no descenders ("Attendance Live Monitor") puts its ink in the upper part of the box while the
 * icon's ink fills its box symmetrically.
 *
 * MEASURED, by decoding the owner's screenshot at 2x and reading dark-pixel extents:
 *
 *   handle ink  y  98..117   centre 107.5      title ink  y  98..113   centre 105.5
 *   handle ink  y 681..700   centre 690.5      title ink  y 681..701   centre 691.0
 *
 * Both inks' TOP edges coincided exactly while the handle hung lower — the signature of
 * top-alignment, which is what was reported. 2 image px at 2x = 1 CSS px, so the handle rises 1px.
 *
 * `lineHeight: 1` on the title was tried first and is a NO-OP here: the row's height comes from the
 * taller actions button, and half-leading shifts the em box by exactly the amount the smaller line
 * box shifts it back — em box top is 7.8px either way. Shipping it would have been a fix that
 * changes nothing, with a comment claiming otherwise.
 *
 * It is a TOKEN and not an inline `mt: '-1px'` for the reason the rest of this pass exists: one
 * declaration, one derivation, and a gate that fails if it is scattered.
 */
export const WIDGET_HANDLE_OPTICAL_RISE = 1;
/**
 * An outlined field's label stays in the notch, filled or empty.
 *
 * MUI draws it INSIDE the box while the field is empty and unfocused, so a panel
 * with some fields filled and some empty shows two apparent kinds of control —
 * the owner read exactly that on the condition node's properties (2026-08-23) and
 * on the requester preview, where the DATE branch pinned this and eight sibling
 * branches did not. A field with both a label and a placeholder is worse: MUI
 * hides the placeholder while the label is inside, so the hint is unreachable.
 *
 * Spread as `{...ALWAYS_SHRINK}` rather than retyped, because eight call sites
 * each deciding this is how it drifted in the first place (svdy-ui §17).
 */
export const ALWAYS_SHRINK = { InputLabelProps: { shrink: true } } as const;

/**
 * The width of a request form in the worker portal.
 *
 * `WorkerSubmitRequest` renders it at this width inside its own bordered Paper,
 * and `ProcessFormPreview`'s desktop frame must match — a preview that runs the
 * full 1 500px of the Review card with no border is a different layout, not a
 * preview of that form. One constant so the two cannot drift (svdy-rules §10.5).
 */
export const PORTAL_FORM_MAX_WIDTH = 720;

/**
 * The height of a Designer wizard stage (Form canvas, Workflow canvas).
 *
 * ONE declaration, because there were five with TWO different offsets:
 * `ProcessDesigner` wrapped step 3 in `calc(100vh - 200px)` while `FlowEditor`'s
 * own root asked for `calc(100vh - 180px)` — the child wanted 20px MORE than its
 * parent gave it, so the canvas overflowed its container. `WizardFormBuilder`
 * repeated the 180 three times, once per column. A contract test already pinned
 * "must not be 140px", which is the tell that the number had drifted before
 * (svdy-ui §17).
 *
 * 200 is the measured chrome above the stage: top nav 56 + page header 52 +
 * wizard stepper 56 + gutters 36. `minHeight` keeps a short viewport usable; the
 * canvas is unreadable below it either way, and scrolling to the graph beats
 * squeezing it to 3px type.
 */
/**
 * The Designer canvas row's VERTICAL box, in one place because it is now written twice:
 * once for a loose field's card and once for a field inside a template group.
 *
 * Owner screenshot 2026-08-27 20:06, measured off the pixels at 2x: loose rows sat
 * **46 CSS px** apart and grouped members **32**. Decomposed, the 14px gap is exactly the
 * chrome a loose row has and a member did not — the card's `p: 0.75` top and bottom (12)
 * plus its two 1px borders. Nothing was arbitrary; the two rows were simply built from
 * different numbers at two call sites, which is the defect class §17 exists for.
 *
 * A group SEPARATES its members with a hairline instead of an 8px gap (owner: 要有下划线做
 * 隔离和区分), so the member's box carries the border on one side only and the arithmetic
 * has to account for it here rather than at the call site.
 */
export const CANVAS_ROW = {
  /** Vertical padding inside the row, matching the loose card's `p: 0.75`. */
  padY: 0.75,
  /** The content wrapper's own padding, inside `padY`. */
  contentPadY: 0.25,
  /** Gap between BLOCKS (a loose row, or a whole group) on the canvas grid. */
  blockGap: 1,
} as const;

export const DESIGNER_STAGE_HEIGHT = {
  height: 'calc(100vh - 200px)',
  minHeight: 500,
} as const;
